/**
 * PlayerPhysics - Player movement and physics resolution
 *
 * Handles:
 * - Movement physics (gravity, velocity)
 * - Collision resolution (separated from detection)
 * - Collision resolution (no auto-step/clamp assistance)
 */

export class PlayerPhysics {
  constructor(tileSize = 12) {
    this.TILE_SIZE = tileSize;

  }

  /**
   * Resolve horizontal collision
   * @param {Object} player - Player state { x, y, w, h, vx, onGround }
   * @param {number} direction - Movement direction
   * @param {Object} collision - Collision system
   * @param {Function} getTileSolid - Tile check function
   * @param {number} groundTopY - Ground surface Y
   * @param {boolean} isUnderground - Is player underground
   * @param {boolean} jumpHeld - Is jump button held
   * @param {Function} checkAboveGround - Optional callback to check non-tile solids above ground (planks, platforms)
   * @returns {Object} { x, y, stopVx, grounded }
   */
  resolveHorizontal(player, direction, collision, getTileSolid, groundTopY, isUnderground, jumpHeld, checkAboveGround = null) {
   let rx = player.x;
   let ry = player.y;
   const rw = player.w;
   const rh = player.h;
   let onGround = false;
   let stopVx = false;
    let stopVy = false;

    // Push back if overlapping (no margin - use full hitbox)
    if (collision.rectOverlapsSolid(
      getTileSolid,
      rx,
      ry,
      rw,
      rh,
      groundTopY,
      checkAboveGround
    )) {
      let moved = 0;
      while (
        collision.rectOverlapsSolid(
          getTileSolid,
          rx,
          ry,
          rw,
          rh,
          groundTopY,
          checkAboveGround
        ) &&
        moved < this.TILE_SIZE
      ) {
        rx -= direction;
        moved++;
      }
      if (moved > 0) stopVx = true;
    }

    return { x: rx, y: ry, stopVx, stopVy, grounded: onGround };
  }

  /**
   * Resolve vertical collision
   */
  resolveVertical(player, collision, getTileSolid, groundTopY, checkAboveGround = null) {
    let ry = player.y;
    const rx = player.x;
    const rw = player.w;
    const rh = player.h;
    let stopVy = false;
    let onGround = false;

    const dirY = Math.sign(player.vy);
    if (dirY === 0) return { y: ry, stopVy, grounded: onGround };

    // Only resolve if actually overlapping (no margin - use full hitbox)
    if (collision.rectOverlapsSolid(
      getTileSolid,
      rx,
      ry,
      rw,
      rh,
      groundTopY,
      checkAboveGround
    )) {
      // If overlap is likely from a side wall while barely moving vertically, skip to avoid wall climb
      const sideDx = player.vx > 0 ? 1 : (player.vx < 0 ? -1 : 0);
      if (sideDx !== 0 && Math.abs(player.vy) < 40) {
        const sideOverlap = collision.rectOverlapsSolid(
          getTileSolid,
          rx + sideDx,
          ry,
          rw,
          rh,
          groundTopY,
          checkAboveGround
        );
        if (sideOverlap) {
          return { y: ry, stopVy, grounded: onGround };
        }
      }
      const stepY = Math.min(
        Math.max(1, Math.floor(Math.abs(player.vy) * 0.016)),
        Math.max(1, Math.floor(this.TILE_SIZE / 3))
      );

      let movedY = 0;
      while (
        collision.rectOverlapsSolid(
          getTileSolid,
          rx,
          ry,
          rw,
          rh,
          groundTopY,
          checkAboveGround
        ) &&
        movedY < this.TILE_SIZE
      ) {
        ry -= dirY * stepY;
        movedY += stepY;
      }

      if (movedY > 0) {
        if (dirY > 0) onGround = true; // Hit ground
        stopVy = true;
      }
    }

    return { y: ry, stopVy, grounded: onGround };
  }

  /**
   * Emergency unstuck (only if truly stuck)
   */
  unstuck(player, collision, getTileSolid, groundTopY, inGracePeriod, preferredDir = 0, isUnderground = false, checkAboveGround = null) {
    // Skip during dig grace period to prevent teleporting
    if (inGracePeriod) return { x: player.x, y: player.y };

    const centerMargin = Math.floor(Math.min(player.w, player.h) * 0.3);
    const centerW = Math.max(2, player.w - centerMargin * 2);
    const centerH = Math.max(2, player.h - centerMargin * 2);

    // Only check center of player
    if (!collision.rectOverlapsSolid(
      getTileSolid,
      player.x + centerMargin,
      player.y + centerMargin,
      centerW,
      centerH,
      groundTopY,
      checkAboveGround
    )) {
      return { x: player.x, y: player.y }; // Not stuck
    }

    // Find nearest clear position
    const maxDist = isUnderground ? 6 : 3; // allow a slightly wider search underground
    const result = collision.findNearestClearPosition(
      getTileSolid,
      player.x,
      player.y,
      player.w,
      player.h,
      groundTopY,
      maxDist,
      preferredDir,
      isUnderground,
      checkAboveGround
    );

    return { x: result.x, y: result.y };
  }
}
